import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import WhatsAppCommunityCard from "../components/WhatsAppCommunityCard";
import {
  AI_EXPERIENCE,
  AI_SURVEY_AGES,
  AI_SURVEY_AREAS,
  AI_SURVEY_DRAFT_KEY,
  AI_SURVEY_STEPS,
  AI_SURVEY_STORAGE_KEY,
  BARRIERS,
  CONTACT_METHODS,
  CONTACT_PERMISSION,
  DELIVERY_PREFERENCES,
  DEVICE_ACCESS,
  DIGITAL_CONFIDENCE,
  emptyAiSurveyAnswers,
  getPriorityOptions,
  MOTIVATIONS,
  PREFERRED_TIMES,
  REFERRAL_SOURCES,
  RIVERSIDE_OPTIONS,
  validateAiDigitalSkillsSurvey,
  WORKSHOP_INTEREST,
  WORKSHOP_TOPICS,
  YOUTH_INTEREST,
} from "./aiDigitalSkillsConfig";
import { submitAiDigitalSkillsSurvey } from "./service";
import { CheckboxGroup, OtherText, RadioGroup } from "./SurveyControls";
import "./communityVoice.css";
import "./formControls.css";

function readStored(key, fallback) {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
}

export default function AiDigitalSkillsSurvey() {
  const [values, setValues] = useState(() => ({
    ...emptyAiSurveyAnswers,
    ...readStored(AI_SURVEY_DRAFT_KEY, {}),
  }));
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [completion, setCompletion] = useState(null);
  const errorSummary = useRef(null);
  const development = process.env.NODE_ENV !== "production";
  const priorityOptions = getPriorityOptions(values);
  const progress = Math.round(((step + 1) / AI_SURVEY_STEPS.length) * 100);

  useEffect(() => {
    if (!completion) localStorage.setItem(AI_SURVEY_DRAFT_KEY, JSON.stringify(values));
  }, [values, completion]);

  useEffect(() => {
    // Earlier versions stored a permanent completion flag. Remove it so shared
    // outreach devices always reopen ready for the next resident.
    localStorage.removeItem(AI_SURVEY_STORAGE_KEY);
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length) errorSummary.current?.focus();
  }, [errors]);

  const set = (key, value) => {
    setValues((current) => {
      const next = { ...current, [key]: value };
      if (key === "topics" || key === "topicsOther") {
        const priorities = getPriorityOptions(next);
        if (next.priorityTopic && !priorities.includes(next.priorityTopic)) {
          next.priorityTopic = "";
        }
      }
      if (key === "contactPermission" && value === "No") {
        next.contactName = "";
        next.contactEmail = "";
        next.contactMobile = "";
        next.preferredContactMethod = "";
        next.contactConsent = false;
      }
      return next;
    });
  };

  const goToStep = (nextStep) => {
    setErrors({});
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextStep = () => {
    const nextErrors = validateAiDigitalSkillsSurvey(values, step);
    setErrors(nextErrors);
    if (!Object.keys(nextErrors).length) goToStep(step + 1);
  };

  async function submit(event) {
    event.preventDefault();
    const nextErrors = validateAiDigitalSkillsSurvey(values);
    setErrors(nextErrors);
    setSubmitError("");
    if (Object.keys(nextErrors).length) {
      const firstErrorStep = AI_SURVEY_STEPS.findIndex((surveyStep) =>
        surveyStep.fields.some((field) => nextErrors[field])
      );

      if (firstErrorStep >= 0) {
        setStep(firstErrorStep);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }

      return;
    }

    setSaving(true);
    try {
      const result = await submitAiDigitalSkillsSurvey(values, development);
      localStorage.removeItem(AI_SURVEY_DRAFT_KEY);
      setCompletion(result);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setSubmitError(
        development
          ? "Your answers could not be saved because the local Firebase survey setup is not ready. Your answers remain on this page."
          : "We could not save your answers. Check your connection and try again."
      );
    } finally {
      setSaving(false);
    }
  }

  const startAnotherResponse = () => {
    localStorage.removeItem(AI_SURVEY_STORAGE_KEY);
    localStorage.removeItem(AI_SURVEY_DRAFT_KEY);
    setValues({ ...emptyAiSurveyAnswers });
    setCompletion(null);
    setStep(0);
    setErrors({});
    setSubmitError("");
    setSaving(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (completion) {
    return (
      <div className="cv-page">
        <section className="cv-complete" aria-live="polite">
          <CheckCircle2 size={64} aria-hidden="true" />
          <p className="cv-kicker">UUN Community Voice</p>
          <h1>Thank you for helping shape local digital skills workshops.</h1>
          <p>Your response has been recorded. UUN will use the findings to plan workshops and demonstrate local demand to potential funders and partners.</p>
          {completion.contactRequested ? <p>Because you opted in, UUN may contact you when workshop information becomes available.</p> : null}
          <div className="cv-repeat-response">
            <h2>Helping more than one person?</h2>
            <p>Use this device again for the next resident. Each submission is saved as a separate response.</p>
            <button type="button" className="cv-button" onClick={startAnotherResponse}>Complete another response on this device</button>
          </div>
          <WhatsAppCommunityCard
            title="Stay connected with UUN"
            description="Join the Urban Unity Network WhatsApp Community for local events, activities, opportunities and community updates."
            showOptionalNotice
            compact
            source="ai-digital-skills-survey-completion"
          />
          <Link className="cv-button cv-secondary" to="/community-voice">Return to Community Voice</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="cv-page cv-ai-survey">
      <header className="cv-survey-head">
        <p className="cv-kicker">UUN Community Voice · approximately 3 minutes</p>
        <h1>Help shape local AI, coding and digital skills workshops</h1>
        <p>Urban Unity Network is exploring free or affordable AI, coding and digital skills workshops for residents in the Isle of Dogs and Tower Hamlets.</p>
        <p>We want to know what you would like to learn, when you could attend and what support would make it easier for you to participate.</p>
        <p>Your answers will help us design programmes around the needs of local residents and demonstrate demand to organisations that may help fund the workshops.</p>
      </header>

      <div className="cv-progress-wrap">
        <div className="cv-progress-copy">
          <strong>Step {step + 1} of {AI_SURVEY_STEPS.length}</strong>
          <span>{AI_SURVEY_STEPS[step].title}</span>
        </div>
        <div className="cv-progress" role="progressbar" aria-label="Survey progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow={progress}>
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>

      <form className="cv-form" onSubmit={submit} noValidate>
        {Object.keys(errors).length ? (
          <div className="cv-summary" ref={errorSummary} tabIndex="-1" role="alert">
            <h2>Please check your answers</h2>
            <p>{Object.keys(errors).length} answer(s) need attention before you continue.</p>
          </div>
        ) : null}

        {step === 0 ? (
          <>
            <RadioGroup legend="1. Would you be interested in attending free or affordable local AI, coding or digital skills workshops? *" name="workshopInterest" items={WORKSHOP_INTEREST} value={values.workshopInterest} onChange={(value) => set("workshopInterest", value)} error={errors.workshopInterest} />
            <CheckboxGroup legend="2. Which workshops would you be interested in? *" items={WORKSHOP_TOPICS} value={values.topics} onChange={(value) => set("topics", value)} error={errors.topics} help="Choose all that interest you." />
            {values.topics.includes("Other") ? <OtherText label="Other workshop" value={values.topicsOther} onChange={(value) => set("topicsOther", value)} error={errors.topicsOther} /> : null}
            <RadioGroup legend="3. Which workshop would you most like us to run first? *" name="priorityTopic" items={priorityOptions} value={values.priorityTopic} onChange={(value) => set("priorityTopic", value)} error={errors.priorityTopic} help={priorityOptions.length ? "Choose one of the topics you selected above." : "Select your workshop interests above first."} />
          </>
        ) : null}

        {step === 1 ? (
          <>
            <RadioGroup legend="4. How confident are you using computers, smartphones and online services? *" name="digitalConfidence" items={DIGITAL_CONFIDENCE} value={values.digitalConfidence} onChange={(value) => set("digitalConfidence", value)} error={errors.digitalConfidence} />
            <RadioGroup legend="5. Have you used AI tools such as ChatGPT, Microsoft Copilot or Google Gemini? *" name="aiExperience" items={AI_EXPERIENCE} value={values.aiExperience} onChange={(value) => set("aiExperience", value)} error={errors.aiExperience} />
            <CheckboxGroup legend="6. What would you like these skills to help you with? *" items={MOTIVATIONS} value={values.motivations} onChange={(value) => set("motivations", value)} error={errors.motivations} help="Choose all that apply." />
            {values.motivations.includes("Other") ? <OtherText label="Other goal" value={values.motivationsOther} onChange={(value) => set("motivationsOther", value)} error={errors.motivationsOther} /> : null}
          </>
        ) : null}

        {step === 2 ? (
          <>
            <CheckboxGroup legend="7. How would you prefer workshops to be delivered? *" items={DELIVERY_PREFERENCES} value={values.deliveryPreference} onChange={(value) => set("deliveryPreference", value)} error={errors.deliveryPreference} help="Choose all that would work for you." />
            <CheckboxGroup legend="8. When would you be most likely to attend? *" items={PREFERRED_TIMES} value={values.preferredTimes} onChange={(value) => set("preferredTimes", value)} error={errors.preferredTimes} help="Choose all that would work for you." />
            <CheckboxGroup legend="9. Would anything make it difficult for you to attend? *" items={BARRIERS} value={values.barriers} onChange={(value) => set("barriers", value)} error={errors.barriers} help="Choose all that apply." />
            {values.barriers.includes("Other") ? <OtherText label="Other barrier" value={values.barriersOther} onChange={(value) => set("barriersOther", value)} error={errors.barriersOther} /> : null}
            <RadioGroup legend="10. Do you have access to a laptop or computer you could use for learning? *" name="deviceAccess" items={DEVICE_ACCESS} value={values.deviceAccess} onChange={(value) => set("deviceAccess", value)} error={errors.deviceAccess} />
          </>
        ) : null}

        {step === 3 ? (
          <>
            <RadioGroup legend="11. Which area do you live in? *" name="area" items={AI_SURVEY_AREAS} value={values.area} onChange={(value) => set("area", value)} error={errors.area} />
            <RadioGroup legend="12. Are you a Riverside Housing resident? *" name="riversideResident" items={RIVERSIDE_OPTIONS} value={values.riversideResident} onChange={(value) => set("riversideResident", value)} error={errors.riversideResident} />
            <RadioGroup legend="13. What is your age group? *" name="ageGroup" items={AI_SURVEY_AGES} value={values.ageGroup} onChange={(value) => set("ageGroup", value)} error={errors.ageGroup} />
            <RadioGroup legend="14. Would a child or young person in your household be interested in coding, AI or technology activities? *" name="youthInterest" items={YOUTH_INTEREST} value={values.youthInterest} onChange={(value) => set("youthInterest", value)} error={errors.youthInterest} />
          </>
        ) : null}

        {step === 4 ? (
          <>
            <RadioGroup legend="Would you like UUN to let you know when these workshops become available? *" name="contactPermission" items={CONTACT_PERMISSION} value={values.contactPermission} onChange={(value) => set("contactPermission", value)} error={errors.contactPermission} />
            {values.contactPermission === "Yes" ? (
              <section className="cv-question cv-contact-panel" aria-labelledby="contact-details-heading">
                <h2 id="contact-details-heading">Your contact details</h2>
                <p className="cv-help">These details are kept separate from anonymous survey reporting.</p>
                <label>Name *<input type="text" autoComplete="name" maxLength="120" value={values.contactName} onChange={(event) => set("contactName", event.target.value)} />{errors.contactName ? <span className="cv-error">{errors.contactName}</span> : null}</label>
                <label>Email<input type="email" autoComplete="email" maxLength="254" value={values.contactEmail} onChange={(event) => set("contactEmail", event.target.value)} />{errors.contactEmail ? <span className="cv-error">{errors.contactEmail}</span> : null}</label>
                <label>Mobile number<input type="tel" autoComplete="tel" maxLength="40" value={values.contactMobile} onChange={(event) => set("contactMobile", event.target.value)} />{errors.contactMobile ? <span className="cv-error">{errors.contactMobile}</span> : null}</label>
                <RadioGroup legend="Preferred contact method *" name="preferredContactMethod" items={CONTACT_METHODS} value={values.preferredContactMethod} onChange={(value) => set("preferredContactMethod", value)} error={errors.preferredContactMethod} />
                <label className="cv-consent"><input type="checkbox" checked={values.contactConsent} onChange={(event) => set("contactConsent", event.target.checked)} /><span>I consent to Urban Unity Network using these contact details to tell me about AI, coding and digital skills workshops.</span></label>
                {errors.contactConsent ? <p className="cv-error">{errors.contactConsent}</p> : null}
              </section>
            ) : null}
            <label className="cv-question"><span>Is there an AI, coding or digital workshop you would particularly like UUN to offer?</span><textarea rows="5" maxLength="1000" value={values.finalComments} onChange={(event) => set("finalComments", event.target.value)} /><small>{values.finalComments.length}/1,000 characters</small>{errors.finalComments ? <span className="cv-error">{errors.finalComments}</span> : null}</label>
            <RadioGroup legend="How did you hear about this survey?" name="referralSource" items={REFERRAL_SOURCES} value={values.referralSource} onChange={(value) => set("referralSource", value)} error={errors.referralSource} />
            {values.referralSource === "Other" ? <OtherText label="Other referral source" value={values.referralSourceOther} onChange={(value) => set("referralSourceOther", value)} error={errors.referralSourceOther} /> : null}
            <div className="cv-privacy">
              UUN will use survey responses to plan local services and workshops. Anonymous findings may be shared with Riverside Housing and other funders or delivery partners. Names and contact details are excluded from anonymous reports and are collected only when you opt in. We do not ask for your home address.
            </div>
          </>
        ) : null}

        {submitError ? <p className="cv-error" role="alert">{submitError}</p> : null}
        <div className="cv-step-actions">
          {step > 0 ? <button type="button" className="cv-button cv-secondary" onClick={() => goToStep(step - 1)}>Back</button> : <Link className="cv-button cv-secondary" to="/community-voice">Exit survey</Link>}
          {step < AI_SURVEY_STEPS.length - 1 ? <button type="button" className="cv-button" onClick={nextStep}>Continue</button> : <button type="submit" className="cv-button" disabled={saving}>{saving ? "Saving your answers…" : "Submit my answers"}</button>}
        </div>
      </form>
    </div>
  );
}
